import { FC } from 'react';

import { UnrecordedResponse } from '@shared/api/giftChannelLedger/giftChannelLedger.interface';
import { DELIVERY_CHANNEL_CONFIG } from '@shared/api/giftCoupon/giftCoupon.interface';

import { kstDateTimeLabel } from '../../utils/kstTime.utils';

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
  from: string;
  setFrom: (value: string) => void;
  to: string;
  setTo: (value: string) => void;
  result: UnrecordedResponse | null;
  loading: boolean;
  onFetch: () => void;
  isRepairing: boolean;
  onRepair: () => void;
}

const formatDateTime = (date: Date) => kstDateTimeLabel(new Date(date));

/**
 * 미기장 발송 점검 — 발송됐는데 원장에 없는 쿠폰을 배치 단위로 검출·소급 기장.
 * 일상 화면이 아니라 접이식. 도입 이전 레거시 발송분은 기초잔액에 흡수돼 있으므로
 * from을 도입일 이후로 줘야 이중 차감이 없다.
 */
export const UnrecordedSection: FC<Props> = ({
  open,
  setOpen,
  from,
  setFrom,
  to,
  setTo,
  result,
  loading,
  onFetch,
  isRepairing,
  onRepair,
}) => {
  const recordableCount = result?.groups.filter((g) => g.recordable).length ?? 0;

  return (
    <div className="rounded-lg border bg-white shadow-sm">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between px-4 py-3 text-left"
      >
        <span className="text-sm font-semibold text-gray-800">
          미기장 발송 점검
          <span className="ml-2 text-xs font-normal text-gray-400">
            발송됐는데 원장에 차감이 없는 건 검출·소급 기장
          </span>
        </span>
        <span className="text-gray-400">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="border-t px-4 py-3">
          <p className="mb-2 text-xs text-amber-600">
            도입 이전 레거시 발송분은 기초잔액에 이미 흡수돼 있습니다 — 조회 시작일을
            반드시 <span className="font-semibold">원장 도입일 이후</span>로 지정하세요.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="rounded border border-gray-300 px-3 py-1.5 text-sm"
            />
            <span className="text-gray-400">~</span>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="rounded border border-gray-300 px-3 py-1.5 text-sm"
            />
            <button
              type="button"
              onClick={onFetch}
              disabled={loading || !from}
              title={!from ? '시작일을 지정해야 조회할 수 있습니다' : undefined}
              className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {loading ? '조회중…' : '조회'}
            </button>
          </div>

          {result && (
            <div className="mt-3">
              {result.totalCoupons === 0 ? (
                <p className="text-sm text-emerald-600">
                  ✓ 기간 내 미기장 발송이 없습니다.
                </p>
              ) : (
                <>
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm font-medium text-red-600">
                      미기장 쿠폰 {result.totalCoupons}건 · {result.groups.length}개 배치
                    </p>
                    <button
                      type="button"
                      onClick={onRepair}
                      disabled={isRepairing || recordableCount === 0}
                      className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {isRepairing
                        ? '소급 기장중…'
                        : `소급 기장 (${recordableCount}배치)`}
                    </button>
                  </div>
                  <div className="overflow-x-auto rounded border">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b bg-gray-50 text-left text-gray-500">
                          <th className="px-3 py-1.5 font-medium">채널</th>
                          <th className="px-3 py-1.5 font-medium">발송 시각</th>
                          <th className="px-3 py-1.5 text-right font-medium">도매가</th>
                          <th className="px-3 py-1.5 text-right font-medium">건수</th>
                          <th className="px-3 py-1.5 text-right font-medium">차감액</th>
                          <th className="px-3 py-1.5 font-medium">소급</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {result.groups.map((group, idx) => (
                          <tr key={idx} className={group.recordable ? '' : 'bg-red-50'}>
                            <td className="whitespace-nowrap px-3 py-1.5">
                              {group.deliveryChannel
                                ? DELIVERY_CHANNEL_CONFIG[group.deliveryChannel].label
                                : '미상'}
                            </td>
                            <td className="whitespace-nowrap px-3 py-1.5 text-gray-600">
                              {formatDateTime(group.deliveredAt)}
                            </td>
                            <td className="whitespace-nowrap px-3 py-1.5 text-right">
                              {group.priceWhole === null
                                ? '미상'
                                : `${group.priceWhole.toLocaleString()}원`}
                            </td>
                            <td className="whitespace-nowrap px-3 py-1.5 text-right">
                              {group.quantity}
                            </td>
                            <td className="whitespace-nowrap px-3 py-1.5 text-right font-medium text-red-600">
                              {group.amount.toLocaleString()}원
                            </td>
                            <td className="whitespace-nowrap px-3 py-1.5">
                              {group.recordable ? (
                                '가능'
                              ) : (
                                <span
                                  className="font-medium text-red-600"
                                  title="채널·도매가 미상 — 쿠폰 내역에서 수동 확인 필요"
                                >
                                  불가
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
